import { languageOf, lowerFirst } from './text-case';

/**
 * Product types that are not gift cards (mobile top-ups, data, bill payments).
 * They are listed next to the gift card categories on the browse page, and
 * must not be described as gift cards.
 */
const nonGiftCardTypes = ['topup', 'data', 'pin', 'bundle', 'digitaltv', 'esim', 'longdistance'];

export const isGiftCardCategory = (slug) => !!slug && !nonGiftCardTypes.includes(slug);

/** The catch-all "All gift cards" entry, as opposed to a real category. */
export const isAllGiftCards = (slug) => slug === 'giftcard' || slug === 'giftcards';

/** Languages that capitalise every noun: a label keeps its capital anywhere. */
const capitalisedNounLanguages = ['de'];

/** Categories named after a brand: a proper noun in every language. */
const properNounCategories = ['uber'];

/** True when `{name}` opens the template or follows the end of a sentence. */
const opensSentence = (template, name) => {
  const index = template.indexOf(`{${name}}`);
  if (index === -1) return true;

  const before = template
    .slice(0, index)
    .replace(/<[^>]+>/g, '')
    .trimEnd();

  return before === '' || /[.!?…¿¡]$/.test(before);
};

/**
 * A category label as it reads inside a message: "Videojuegos" becomes
 * "videojuegos" in "Tarjetas de regalo de {category}", and stays as it is in
 * "{category} gift cards", where it opens the phrase.
 *
 * German, proper nouns ("Uber") and labels that open with an acronym
 * ("TV digitale") keep their capital.
 *
 * @param {string} label  The label from `categories.<slug>`.
 * @param {object} options
 * @param {string} options.template  The raw message the label goes into (`t.raw(key)`).
 * @param {string} options.locale
 * @param {string} [options.slug]  The category slug.
 * @param {string} [options.placeholder]  Defaults to `category`.
 */
export const categoryInPhrase = (label, { template, locale, slug, placeholder = 'category' }) => {
  if (typeof label !== 'string' || typeof template !== 'string') return label;
  if (opensSentence(template, placeholder)) return label;
  if (capitalisedNounLanguages.includes(languageOf(locale))) return label;
  if (properNounCategories.includes(slug)) return label;

  return lowerFirst(label, locale);
};

/**
 * "<Category> gift cards" for headings and titles, e.g. "Gaming gift cards",
 * "Tarjetas de regalo de videojuegos". A category whose name does not read
 * well in that template has its own phrase under
 * `categories.giftCardPhrases.<slug>` ("Gift cards for her").
 *
 * `cT` is `useTranslations('categories')`, `locale` the page's locale.
 */
export const giftCardPhrase = (cT, slug, locale) => {
  if (cT.has(`giftCardPhrases.${slug}`)) {
    return cT(`giftCardPhrases.${slug}`);
  }

  const category = categoryInPhrase(cT(slug), {
    template: cT.raw('giftCardPhrases.default'),
    locale,
    slug,
  });

  return cT('giftCardPhrases.default', { category });
};
